import React, { Fragment, useState } from 'react';
import { IUserMenu, Setting } from './interfaces';
import MailIcon from '@mui/icons-material/Mail';
import {
  Avatar,
  Badge,
  Box,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../../context/AuthProvider';
import { useDevelopmentModeToggle } from '../../../../context/DevelopmentModeProvider';
import { getCurrentUserAsMember } from '@helpers/memberHelper';
import { ROUTES } from '@routes/routes';
import i18n from '@translations/i18n';
import { useTranslation } from 'react-i18next';
import { useUpdateMyLocaleMutation } from '@reactQuery/auth';
import { MemberLocale } from '@api/memberApi/interfaces';
import { NotificationMenu } from '../NotificationMenu';
import { useInDevMode } from '@hooks/useInDevMode';

export const UserMenu: React.FC<IUserMenu> = (): JSX.Element | null => {
  const navigate = useNavigate();
  const { handleLogout, currentUser } = useAuth();
  const { inDevMode, setInDevMode } = useDevelopmentModeToggle();
  const canUseDevelopmentMode = !!currentUser?.isSuperAdmin;
  const inDevModeForFeatures = useInDevMode();
  const hideHeaderMenu = useMediaQuery('(max-width: 500px)');
  const member = getCurrentUserAsMember();
  const lang = i18n.language;
  const { t } = useTranslation();
  const { mutateAsync: updateMyLocale, isPending: isUpdatingLocale } =
    useUpdateMyLocaleMutation();

  const onLogoutClick = async (): Promise<void> => {
    await handleLogout();
  };

  const settings: Setting[] = [
    {
      name: 'Profiel',
      link: '/profile',
    },
    ...(window.deferredPrompt
      ? [
          {
            name: t('userMenu:install-mobile-app'),
            action: async () => {
              window.deferredPrompt.prompt();
              await window.deferredPrompt.userChoice;
            },
          },
        ]
      : []),
    {
      name: 'Instellingen',
    },
    {
      name: 'Uitloggen',
      action: onLogoutClick,
    },
  ];

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOnUserMenuOptionClick = (setting: Setting): void => {
    handleCloseUserMenu();

    if (setting.action) {
      setting.action();
    }

    if (setting.link) {
      navigate(setting.link);
    }
  };

  if (!member) return null;

  return (
    <Box
      sx={{
        flexGrow: 0,
        marginLeft: 'auto',
        gap: '5px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {!hideHeaderMenu && (
        <Fragment>
          <NotificationMenu />

          {inDevModeForFeatures && (
            <IconButton
              aria-label="show 4 new mails"
              color="default"
              sx={{
                width: 40,
                height: 40,
              }}
            >
              <Badge
                badgeContent={0}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: '16px',
                    minWidth: '16px',
                  },
                }}
              >
                <MailIcon fontSize="small" />
              </Badge>
            </IconButton>
          )}
        </Fragment>
      )}

      <Tooltip title="Jouw instellingen">
        <IconButton
          onClick={handleOpenUserMenu}
          sx={{ p: '5px', ml: { sx: 0, md: '10px' } }}
        >
          <Avatar alt={member.name} src={ROUTES.images.avatar(member.slug)} />
        </IconButton>
      </Tooltip>

      <Menu
        sx={{ mt: '45px' }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        {canUseDevelopmentMode ? (
          <MenuItem>
            <FormControlLabel
              control={
                <Switch
                  checked={inDevMode}
                  onChange={(e) => {
                    setInDevMode(e.target.checked);
                  }}
                />
              }
              label="Development"
            />
          </MenuItem>
        ) : null}
        <MenuItem>
          <FormControlLabel
            control={
              <Switch
                checked={lang === 'en'}
                disabled={isUpdatingLocale}
                onChange={async () => {
                  const nextLocale: MemberLocale = lang === 'en' ? 'nl' : 'en';

                  await updateMyLocale(nextLocale);
                  await i18n.changeLanguage(nextLocale);
                  window.location.reload();
                }}
              />
            }
            label="Engels"
          />
        </MenuItem>
        {settings.map((setting) => (
          <MenuItem
            key={setting.name}
            onClick={() => handleOnUserMenuOptionClick(setting)}
          >
            <Typography textAlign="center">{setting.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
